import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Typography } from "@material-tailwind/react";
import { motion } from "framer-motion";
import type { SystemType } from "../../types/types";
import { statusTextColorMap } from "../../types/types";
import { formatTimestamp } from "../../utils/formatTime";
import { useTranslation } from "react-i18next";
import { fetchClusters } from "../../store/slices/clustersSlice";
import {
  fetchPodMetricsHistory,
  fetchPodMetrics,
} from "../../store/slices/podMetricsSlice";
import type { PodMetric } from "../../store/slices/podMetricsSlice";
import { fetchKafkaLag } from "../../store/slices/kafkaLagSlice";
import { fetchMaintenanceStatus } from "../../store/slices/maintenanceSlice";
import { fetchCriticalServices } from "../../store/slices/criticalServicesSlice";
import { fetchConfigByName } from "../../store/slices/configSlice";
import type { RootState, AppDispatch } from "../../store";
import {
  Menu,
  MenuHandler,
  MenuList,
  MenuItem,
  Button,
} from "@material-tailwind/react";
import PodServiceCard from "./PodServiceCard";
import MonthNavigator from "./MonthNavigator";
import Loading from "./Loading";
import { Memory16Regular, ProductionRegular } from "@fluentui/react-icons";
import HistoricalMetricsDialog from "./HistoricalMetricsDialog";
import StatusCalendarTable from "./StatusHistoryTable";

export const StatusPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { clusters = [], loading: clustersLoading } = useSelector(
    (state: RootState) => state.clusters
  );
  const {
    metrics = [],
    historicalMetrics = [],
    loading: metricsLoading,
    loadingHistory,
    error: metricsError,
  } = useSelector((state: RootState) => state.podMetrics);
  const { metrics: kafkaLagMetrics = [] } = useSelector(
    (state: RootState) => state.kafkaLag
  );
  const { globalMaintenance, activeSessions, serviceMaintenance } = useSelector(
    (state: RootState) => state.maintenance
  );
  
  // Get critical services from Redux store
  const { criticalServices } = useSelector(
    (state: RootState) => state.criticalServices
  );
  
  // Redux state for configuration
  useSelector((state: RootState) => state.config);
  
  // State for refresh time intervals
  const [normalRefreshTime, setNormalRefreshTime] = useState<number>(5); // Default 5 minutes
  const [maintenanceRefreshTime, setMaintenanceRefreshTime] = useState<number>(1); // Default 1 minute

  const [metricsTimestamp, setMetricsTimestamp] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear()
  );
  const [selectedMonth, setSelectedMonth] = useState<number>(
    new Date().getMonth()
  );
  const [systemType, setSystemType] = useState<SystemType>("prod");
  const [lastUpdated, setLastUpdated] = useState<string>(
    new Date().toISOString()
  );
  const [scrollY, setScrollY] = useState(0);
  const { t } = useTranslation();

  // Get current day for highlighting in the table
  const currentDay = new Date().getDate();
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();

  // Variable to track today's date for special highlighting
  const todayDate = new Date().getDate();

  // Add scroll handler
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Determine if we're in the scrolled state (when header is compact)
  const isScrolled = scrollY > 100;

  // Fetch clusters on component mount
  useEffect(() => {
    dispatch(fetchClusters());
  }, [dispatch]);
  
  // Fetch configuration values for refresh times
  useEffect(() => {
    // Fetch normal refresh time
    dispatch(fetchConfigByName('refresh_time'))
      .unwrap()
      .then((config) => {
        if (config && typeof config.value === 'number') {
          setNormalRefreshTime(config.value);
        }
      })
      .catch((err) => console.error('Error fetching refresh_time:', err));
    
    // Fetch maintenance refresh time
    dispatch(fetchConfigByName('maintenance_refresh_time'))
      .unwrap()
      .then((config) => {
        if (config && typeof config.value === 'number') {
          setMaintenanceRefreshTime(config.value);
        }
      })
      .catch((err) => console.error('Error fetching maintenance_refresh_time:', err));
  }, [dispatch]);

  // Separate useEffect for the initial data fetch
  useEffect(() => {
    if (clusters && clusters.length > 0 && clusters.includes(systemType)) {
      // Fetch historical metrics for the entire month
      const startDate = new Date(selectedYear, selectedMonth, 1);
      const startTime = startDate.toISOString();

      // Fetch all the data
      dispatch(fetchPodMetrics(systemType));
      dispatch(fetchKafkaLag(systemType));
      dispatch(fetchMaintenanceStatus(systemType));
      dispatch(fetchCriticalServices(systemType));
      dispatch(
        fetchPodMetricsHistory({
          cluster: systemType,
          start_time: startTime,
        })
      );
      
      setMetricsTimestamp(new Date().toISOString());
      setLastUpdated(new Date().toISOString());
    }
  }, [dispatch, systemType, clusters, selectedYear, selectedMonth]);
  
  // Separate useEffect for handling the refresh interval to prevent multiple intervals
  useEffect(() => {
    if (clusters && clusters.length > 0 && clusters.includes(systemType)) {
      const fetchMetrics = async () => {
        dispatch(fetchPodMetrics(systemType));
        dispatch(fetchKafkaLag(systemType));
        dispatch(fetchMaintenanceStatus(systemType));
        dispatch(fetchCriticalServices(systemType));
        setMetricsTimestamp(new Date().toISOString());
        setLastUpdated(new Date().toISOString());
      };

      // Calculate current refresh interval in milliseconds
      const inMaintenance = isInMaintenanceMode();
      const refreshMinutes = inMaintenance ? maintenanceRefreshTime : normalRefreshTime;
      const refreshMs = refreshMinutes * 60 * 1000; 
      // Set up refresh interval
      const metricsRefreshInterval = setInterval(fetchMetrics, refreshMs);

      // Clean up interval on unmount or when dependencies change
      return () => {
        clearInterval(metricsRefreshInterval);
      };
    }
  }, [
    // Only include dependencies that should reset the timer
    systemType, 
    normalRefreshTime, 
    maintenanceRefreshTime,
    globalMaintenance?.id,
    activeSessions.length
  ]);

  // Helper function to check if maintenance is currently active based on time range
  const isMaintenanceActive = (session: any) => {
    if (!session || !session.start_time || !session.end_time) return false;
    
    const now = new Date().getTime();
    const start = new Date(session.start_time).getTime();
    const end = new Date(session.end_time).getTime();
    
    return now >= start && now <= end;
  };
  
  // Helper function to check if system is in maintenance mode (global or any service)
  const isInMaintenanceMode = () => {
    // Check if global maintenance is active
    if (globalMaintenance?.enabled && isMaintenanceActive(globalMaintenance)) {
      return true;
    }
    
    // Check if any service is in maintenance mode
    if (activeSessions && activeSessions.length > 0) {
      return activeSessions.some(session => 
        session.type === 'service' && 
        session.enabled && 
        isMaintenanceActive(session)
      );
    }
    
    return false;
  };

  // Get overall system status based on pod metrics, Kafka lag, and maintenance status
  const getSystemStatus = () => {
    if (metricsLoading)
      return {
        prefix: "",
        status: t("Loading..."),
        color: "text-blue-gray-500",
      };
    if (metricsError)
      return {
        prefix: t("Error"),
        status: t("Loading Metrics"),
        color: "text-red-500",
      };

    // Check if global maintenance is active and within time range
    if (globalMaintenance?.enabled && isMaintenanceActive(globalMaintenance)) {
      return {
        prefix: t("System in"),
        status: t("Maintenance"),
        color: "text-blue-500",
      };
    }
    
    // Check if any service is in maintenance and within time range
    const hasServiceInMaintenance = activeSessions && activeSessions.length > 0 && 
      activeSessions.some(session => 
        session.type === 'service' && 
        session.enabled && 
        isMaintenanceActive(session)
      );
    
    if (hasServiceInMaintenance) {
      return {
        prefix: t("Service in"),
        status: t("Maintenance"),
        color: "text-blue-500",
      };
    }

    // Group lag metrics by clientId and sum them
    const lagSumByClientId = kafkaLagMetrics.reduce(
      (acc: Record<string, number>, curr) => {
        const clientId = curr.clientId;
        if (!acc[clientId]) {
          acc[clientId] = 0;
        }
        acc[clientId] += curr.lag || 0;
        return acc;
      },
      {}
    );

    // Check for Kafka lag issues
    const hasHighLag = Object.values(lagSumByClientId).some(
      (totalLag) => totalLag > 10000
    );
    const hasModLag = Object.values(lagSumByClientId).some(
      (totalLag) => totalLag > 1000 && totalLag <= 10000
    );

    // Check for pod status issues
    const hasFailedPods = metrics.some((pod) => pod.phase === "Failed");
    const hasPendingPods = metrics.some((pod) => pod.phase === "Pending");

    if (hasHighLag || hasFailedPods)
      return {
        prefix: t("System"),
        status: t("Down"),
        color: statusTextColorMap["Failed"],
      };

    if (hasModLag || hasPendingPods)
      return {
        prefix: t("System"),
        status: t("Degraded"),
        color: statusTextColorMap["Pending"],
      };

    // If no issues found, system is operational
    return {
      prefix: t("All systems are"),
      status: t("Operational"),
      color: statusTextColorMap["Running"],
    };
  };

  // Generate days array for the calendar header based on the selected month and year
  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const days = Array.from(
    { length: getDaysInMonth(selectedYear, selectedMonth) },
    (_, i) => i + 1
  );

  const handleMonthChange = (year: number, month: number) => {
    setSelectedYear(year);
    setSelectedMonth(month);

    // Fetch historical metrics for the entire month
    if (clusters && clusters.length > 0 && clusters.includes(systemType)) {
      // Get the first day of the selected month
      const startDate = new Date(year, month, 1);
      const startTime = startDate.toISOString();

      dispatch(
        fetchPodMetricsHistory({
          cluster: systemType,
          start_time: startTime,
        })
      );
    }
  };

  // Group pod metrics by container name and check for maintenance status
  const podsByContainer = useMemo(() => {
    const grouped: Record<string, typeof metrics> = {};
    metrics.forEach((pod) => {
      const displayName = pod.display_name || pod.container_name;
      if (!grouped[displayName]) {
        grouped[displayName] = [];
      }
      
      // Create a copy of the pod to avoid modifying the original
      const podCopy = { ...pod };
      
      // Check if this pod's service is in maintenance
      let isInMaintenance = false;
      
      // Check if global maintenance is active
      if (globalMaintenance?.enabled && isMaintenanceActive(globalMaintenance)) {
        isInMaintenance = true;
      } 
      // Check service-specific maintenance
      else if (serviceMaintenance && serviceMaintenance[pod.container_name]) {
        if (isMaintenanceActive(serviceMaintenance[pod.container_name])) {
          isInMaintenance = true;
        }
      }
      // Check active sessions
      else if (activeSessions && activeSessions.length > 0) {
        const containerNameToCheck = pod.container_name.toLowerCase();
        
        isInMaintenance = activeSessions.some(session => {
          if (session.enabled && isMaintenanceActive(session) && 
              session.services && Array.isArray(session.services)) {
            const found = session.services.some(service => 
              service.toLowerCase() === containerNameToCheck
            );
            if (found) {
              return true;
            }
          }
          return false;
        });
      }
      
      // If in maintenance, update the pod phase
      if (isInMaintenance) {
        podCopy.phase = 'Maintenance';
      }
      
      grouped[displayName].push(podCopy);
    });
    return grouped;
  }, [metrics, globalMaintenance, serviceMaintenance, activeSessions]);

  // Create a map of clientId to lag
  const lagByClientId = useMemo(() => {
    const lagMap: Record<string, number> = {};
    kafkaLagMetrics.forEach((lagMetric) => {
      const clientId = lagMetric.clientId;
      if (!lagMap[clientId] || lagMetric.lag > lagMap[clientId]) {
        lagMap[clientId] = lagMetric.lag;
      }
    });
    return lagMap;
  }, [kafkaLagMetrics]);

  // Organize historical metrics by day and pod for efficient lookups
  const metricsByDayAndPod = useMemo(() => {
    // Skip processing if metrics are still loading or empty
    if (loadingHistory || historicalMetrics.length === 0) {
      return {};
    }

    const result: Record<string, PodMetric[]> = {};

    // Process all metrics once
    historicalMetrics.forEach((metric) => {
      const metricDate = new Date(metric.timestamp);
      const day = metricDate.getDate();
      const month = metricDate.getMonth();
      const year = metricDate.getFullYear();

      // Only include metrics for the selected month/year
      if (month === selectedMonth && year === selectedYear) {
        // Create keys for container-day and pod-day lookups
        const containerDayKey = `container-${metric.container_name}-${day}`;
        const podDayKey = `pod-${metric.pod_name}-${day}`;

        // Store metrics by container and day
        if (!result[containerDayKey]) {
          result[containerDayKey] = [];
        }
        result[containerDayKey].push(metric);

        // Store metrics by pod and day
        if (!result[podDayKey]) {
          result[podDayKey] = [];
        }
        result[podDayKey].push(metric);
      }
    });

    return result;
  }, [historicalMetrics, selectedMonth, selectedYear, loadingHistory]);

  // Handle system type change
  const handleSystemTypeChange = (type: SystemType) => {
    setSystemType(type);
    if (clusters && clusters.length > 0 && clusters.includes(type)) {
      dispatch(fetchPodMetrics(type));
      dispatch(fetchKafkaLag(type));
      dispatch(fetchMaintenanceStatus(type));

      // Fetch historical metrics for the entire month
      const startDate = new Date(selectedYear, selectedMonth, 1);
      const startTime = startDate.toISOString();

      dispatch(
        fetchPodMetricsHistory({
          cluster: type,
          start_time: startTime,
        })
      );
    }
  };

  // Add state for historical metrics dialog
  const [historyDialogOpen, setHistoryDialogOpen] = useState<boolean>(false);
  const [selectedHistoricalData, setSelectedHistoricalData] = useState<
    | {
        containerName: string;
        podName?: string;
        metrics: PodMetric[];
        date: Date;
      }
    | undefined
  >(undefined);

  // Handle click on a container or pod to show historical metrics
  const handleHistoricalMetricsClick = (
    containerName: string,
    podName?: string,
    day?: number
  ) => {
    // Use current day if day parameter is not provided
    const targetDay = day || currentDay;

    if (historicalMetrics.length === 0) {
      return;
    }

    // If podName is provided, use pod-specific metrics
    if (podName) {
      // Use the memoized metrics lookup instead of filtering the array each time
      const podDayKey = `pod-${podName}-${targetDay}`;
      const podMetrics = metricsByDayAndPod[podDayKey];

      if (!podMetrics || podMetrics.length === 0) {
        return;
      }

      // Set the selected data and open the dialog
      const targetDate = new Date(selectedYear, selectedMonth, targetDay);

      setSelectedHistoricalData({
        containerName,
        podName,
        metrics: podMetrics,
        date: targetDate,
      });
    }
    // If only containerName is provided, use container metrics
    else {
      // Use the memoized metrics lookup instead of filtering the array each time
      const containerDayKey = `container-${containerName}-${targetDay}`;
      const containerMetrics = metricsByDayAndPod[containerDayKey];

      if (!containerMetrics || containerMetrics.length === 0) {
        return;
      }

      // Set the selected data and open the dialog
      const targetDate = new Date(selectedYear, selectedMonth, targetDay);

      setSelectedHistoricalData({
        containerName,
        metrics: containerMetrics,
        date: targetDate,
      });
    }

    setHistoryDialogOpen(true);
  };

  // Handle closing the historical metrics dialog
  const handleHistoryDialogToggle = () => {
    setHistoryDialogOpen(!historyDialogOpen);
  };

  return (
    <div className="flex flex-col min-h-screen pt-[160px]">
      {" "}
      {/* Add padding-top to account for fixed header */}
      {/* Add the tooltip styles */}
      {/* <style>{tooltipStyles}</style> */}
      {/* Header - Dark and Fixed */}
      <motion.header
        className="bg-[#0C111D] text-white fixed top-0 left-0 right-0 z-50"
        initial={{ height: "160px" }}
        animate={{ height: isScrolled ? "80px" : "160px" }}
        transition={{ duration: 0.3 }}
      >
        <div className="mx-auto px-6 h-full flex items-center">
          <div className="flex flex-row justify-between items-center w-full">
            <div className="flex flex-row items-center">
              <motion.div
                className="rounded-full flex items-center justify-center mr-4"
                initial={{ width: "64px", height: "64px" }}
                animate={{
                  width: isScrolled ? "40px" : "64px",
                  height: isScrolled ? "40px" : "64px",
                }}
                transition={{ duration: 0.3 }}
              >
                {systemType === "dev" ? (
                  <svg
                    width="61"
                    height="42"
                    viewBox="0 0 61 42"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8.40249 8.40847V33.6035H16.7925V25.2881H25.195V41.9935H0V0.00598145H25.195V8.40847H8.40249Z"
                      fill="#12B76A"
                    />
                    <path
                      d="M51.6473 25.2075V16.7925H43.2572V0H34.8547V42H60.0497V35.3651H60.0746V25.2199H51.6473V25.2075ZM51.6473 33.5975H43.2572V25.2075H51.6473V33.5975Z"
                      fill="#12B76A"
                    />
                  </svg>
                ) : (
                  <svg
                    width="61"
                    height="42"
                    viewBox="0 0 61 42"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8.40249 8.4085V33.6035H16.7925V25.2882H25.195V41.9935H0V0.00601196H25.195V8.4085H8.40249Z"
                      fill="#DA1E48"
                    />
                    <path
                      d="M51.6473 25.2075V16.7925H43.2572V1.52588e-05H34.8547V42H60.0497V35.3651H60.0746V25.2199H51.6473V25.2075ZM51.6473 33.5975H43.2572V25.2075H51.6473V33.5975Z"
                      fill="#DA1E48"
                    />
                  </svg>
                )}
              </motion.div>

              <div>
                <div className="flex items-center">
                  <motion.div
                    initial={{ fontSize: "36px" }}
                    animate={{ fontSize: isScrolled ? "24px" : "36px" }}
                    transition={{ duration: 0.3 }}
                  >
                    <Typography
                      variant="h1"
                      className="font-inter font-bold leading-[44px] tracking-[-0.02em] text-[#FCFCFD]"
                      style={{ fontSize: isScrolled ? "24px" : "36px" }}
                      placeholder={undefined}
                      onResize={undefined}
                      onResizeCapture={undefined}
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    >
                      {systemType === "prod"
                        ? t("Production System")
                        : t("Development System")}
                    </Typography>
                  </motion.div>

                  {/* System Type Selector */}
                  <div>
                    <Menu>
                      <MenuHandler>
                        <Button
                          variant="text"
                          className="flex items-center gap-2 bg-transparent text-white py-2 px-3 border-0 outline-none focus:outline-none active:outline-none hover:outline-none ring-0 focus:ring-0 active:ring-0 hover:ring-0 shadow-none focus:shadow-none active:shadow-none hover:shadow-none"
                          placeholder={undefined}
                          onResize={undefined}
                          onResizeCapture={undefined}
                          onPointerEnterCapture={undefined}
                          onPointerLeaveCapture={undefined}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            className="h-6 w-6"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </Button>
                      </MenuHandler>
                      <MenuList
                        placeholder={undefined}
                        onResize={undefined}
                        onResizeCapture={undefined}
                        onPointerEnterCapture={undefined}
                        onPointerLeaveCapture={undefined}
                      >
                        {clustersLoading ? (
                          <MenuItem
                            placeholder={undefined}
                            onResize={undefined}
                            onResizeCapture={undefined}
                            onPointerEnterCapture={undefined}
                            onPointerLeaveCapture={undefined}
                          >
                            {t("Loading clusters...")}
                          </MenuItem>
                        ) : clusters && clusters.length > 0 ? (
                          clusters.map((cluster) => (
                            <MenuItem
                              key={cluster}
                              onClick={() =>
                                handleSystemTypeChange(cluster as SystemType)
                              }
                              placeholder={undefined}
                              onResize={undefined}
                              onResizeCapture={undefined}
                              onPointerEnterCapture={undefined}
                              onPointerLeaveCapture={undefined}
                              className="hover:bg-[#E4E7EC] border-0 outline-none focus:outline-none active:outline-none hover:outline-none ring-0 focus:ring-0 active:ring-0 hover:ring-0 shadow-none focus:shadow-none active:shadow-none hover:shadow-none"
                            >
                              <div className="flex items-center gap-2 font-inter font-semibold text-base text-[#344054] hover:bg-[#E4E7EC]">
                                {cluster === "prod" ? (
                                  <ProductionRegular className="h-4 w-4" />
                                ) : (
                                  <Memory16Regular />
                                )}
                                {cluster === "prod"
                                  ? t("Production")
                                  : t("Development")}
                              </div>
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem
                            placeholder={undefined}
                            onResize={undefined}
                            onResizeCapture={undefined}
                            onPointerEnterCapture={undefined}
                            onPointerLeaveCapture={undefined}
                          >
                            {t("No clusters available")}
                          </MenuItem>
                        )}
                      </MenuList>
                    </Menu>
                  </div>
                </div>
              </div>
            </div>

            {/* Center area for status text */}
            <motion.div
              // className="absolute left-1/2 transform -translate-x-1/2 flex items-center justify-center"
              initial={{ opacity: 0, y: 50 }}
              animate={{
                opacity: isScrolled ? 1 : 0,
                y: isScrolled ? 0 : 50,
                marginRight: isScrolled ? 150 : 0,
              }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex items-center">
                <Typography
                  variant="h3"
                  className="font-inter font-bold text-white mr-1"
                  placeholder={undefined}
                  onResize={undefined}
                  onResizeCapture={undefined}
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                >
                  {getSystemStatus().prefix}
                </Typography>
                <Typography
                  variant="h3"
                  className={`font-inter font-bold ${getSystemStatus().color}`}
                  placeholder={undefined}
                  onResize={undefined}
                  onResizeCapture={undefined}
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                >
                  {getSystemStatus().status}
                </Typography>
              </div>
            </motion.div>

            {/* Only show this when not scrolled */}
            {!isScrolled && (
              <div className="flex flex-row items-center gap-4">
                <div>
                  <Typography
                    variant="h2"
                    className="font-inter font-bold text-[30px] leading-[38px] text-[#FCFCFD] text-right"
                    placeholder={undefined}
                    onResize={undefined}
                    onResizeCapture={undefined}
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  >
                    {t("Update")}
                  </Typography>
                  <p className="text-sm text-gray-400">
                    {t("Last updated")} {formatTimestamp(metricsTimestamp)}{" "}
                    {metricsTimestamp &&
                      (() => {
                        // Calculate next update time using the dynamic refresh interval
                        const inMaintenance = isInMaintenanceMode();
                        const refreshMinutes = inMaintenance ? maintenanceRefreshTime : normalRefreshTime;
                        const refreshMs = refreshMinutes * 60 * 1000;
                        
                        const nextUpdateDate = new Date(
                          new Date(lastUpdated).getTime() + refreshMs
                        );
                        return `Next update at ${nextUpdateDate.toLocaleTimeString()}`;
                      })()}
                  </p>
                </div>
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center cursor-pointer hover:shadow-lg transition-shadow"
                  tabIndex={0}
                  role="button"
                  aria-label="Update"
                  onClick={() => {
                    dispatch(fetchPodMetrics(systemType));
                    dispatch(fetchKafkaLag(systemType));
                    dispatch(fetchMaintenanceStatus(systemType));

                    // Fetch historical metrics for the entire month
                    const startDate = new Date(selectedYear, selectedMonth, 1);
                    const startTime = startDate.toISOString();

                    dispatch(
                      fetchPodMetricsHistory({
                        cluster: systemType,
                        start_time: startTime,
                      })
                    );

                    setMetricsTimestamp(new Date().toISOString());
                  }}
                >
                  <svg
                    width="100%"
                    height="100%"
                    viewBox="0 0 72 72"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect width="72" height="72" rx="8" fill="#12B76A" />
                    <path
                      d="M19.3334 36C19.3334 26.7953 26.7953 19.3333 36 19.3333C45.2048 19.3333 52.6667 26.7953 52.6667 36C52.6667 45.2047 45.2048 52.6667 36 52.6667C26.7953 52.6667 19.3334 45.2047 19.3334 36ZM45.1667 29.75C45.1667 29.0596 44.6071 28.5 43.9167 28.5C43.2264 28.5 42.6667 29.0596 42.6667 29.75V30.9992C41.1463 28.9756 38.726 27.6667 36 27.6667C33.4556 27.6667 31.1766 28.8085 29.65 30.6034C29.2027 31.1293 29.2664 31.9181 29.7922 32.3654C30.3181 32.8127 31.107 32.749 31.5543 32.2231C32.6262 30.9629 34.2195 30.1667 36 30.1667C38.327 30.1667 40.3358 31.5292 41.272 33.5H38.9167C38.2264 33.5 37.6667 34.0596 37.6667 34.75C37.6667 35.4404 38.2264 36 38.9167 36H43.9167C44.6071 36 45.1667 35.4404 45.1667 34.75V29.75ZM28.0834 43.5C28.7737 43.5 29.3334 42.9404 29.3334 42.25V41.0008C30.8538 43.0244 33.274 44.3333 36 44.3333C38.5434 44.3333 40.8215 43.1924 42.3482 41.3989C42.7956 40.8732 42.7322 40.0843 42.2065 39.6368C41.6808 39.1893 40.8919 39.2528 40.4444 39.7785C39.3725 41.0378 37.7798 41.8333 36 41.8333C33.673 41.8333 31.6643 40.4708 30.728 38.5H33.0834C33.7737 38.5 34.3334 37.9404 34.3334 37.25C34.3334 36.5596 33.7737 36 33.0834 36H28.0834C27.393 36 26.8334 36.5596 26.8334 37.25V42.25C26.8334 42.9404 27.393 43.5 28.0834 43.5Z"
                      fill="#FCFCFD"
                    />
                  </svg>
                </div>
              </div>
            )}

            {/* Show update text when scrolled */}
            {isScrolled && (
              <div className="flex flex-row items-center gap-2">
                <Typography
                  variant="h2"
                  className="font-inter font-bold text-[24px] leading-[28px] text-[#FCFCFD]"
                  placeholder={undefined}
                  onResize={undefined}
                  onResizeCapture={undefined}
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                >
                  {t("Update")}
                </Typography>
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => {
                    dispatch(fetchPodMetrics(systemType));
                    dispatch(fetchKafkaLag(systemType));
                    dispatch(fetchMaintenanceStatus(systemType));

                    // Fetch historical metrics for the entire month
                    const startDate = new Date(selectedYear, selectedMonth, 1);
                    const startTime = startDate.toISOString();

                    dispatch(
                      fetchPodMetricsHistory({
                        cluster: systemType,
                        start_time: startTime,
                      })
                    );

                    setMetricsTimestamp(new Date().toISOString());
                  }}
                >
                  <svg
                    width="100%"
                    height="100%"
                    viewBox="0 0 72 72"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect width="72" height="72" rx="8" fill="#12B76A" />
                    <path
                      d="M19.3334 36C19.3334 26.7953 26.7953 19.3333 36 19.3333C45.2048 19.3333 52.6667 26.7953 52.6667 36C52.6667 45.2047 45.2048 52.6667 36 52.6667C26.7953 52.6667 19.3334 45.2047 19.3334 36ZM45.1667 29.75C45.1667 29.0596 44.6071 28.5 43.9167 28.5C43.2264 28.5 42.6667 29.0596 42.6667 29.75V30.9992C41.1463 28.9756 38.726 27.6667 36 27.6667C33.4556 27.6667 31.1766 28.8085 29.65 30.6034C29.2027 31.1293 29.2664 31.9181 29.7922 32.3654C30.3181 32.8127 31.107 32.749 31.5543 32.2231C32.6262 30.9629 34.2195 30.1667 36 30.1667C38.327 30.1667 40.3358 31.5292 41.272 33.5H38.9167C38.2264 33.5 37.6667 34.0596 37.6667 34.75C37.6667 35.4404 38.2264 36 38.9167 36H43.9167C44.6071 36 45.1667 35.4404 45.1667 34.75V29.75ZM28.0834 43.5C28.7737 43.5 29.3334 42.9404 29.3334 42.25V41.0008C30.8538 43.0244 33.274 44.3333 36 44.3333C38.5434 44.3333 40.8215 43.1924 42.3482 41.3989C42.7956 40.8732 42.7322 40.0843 42.2065 39.6368C41.6808 39.1893 40.8919 39.2528 40.4444 39.7785C39.3725 41.0378 37.7798 41.8333 36 41.8333C33.673 41.8333 31.6643 40.4708 30.728 38.5H33.0834C33.7737 38.5 34.3334 37.9404 34.3334 37.25C34.3334 36.5596 33.7737 36 33.0834 36H28.0834C27.393 36 26.8334 36.5596 26.8334 37.25V42.25C26.8334 42.9404 27.393 43.5 28.0834 43.5Z"
                      fill="#FCFCFD"
                    />
                  </svg>
                </div>
              </div>
            )}
          </div>

          {/* Status Banner - Only show when not scrolled */}
          {!isScrolled && (
            <div className="box-border flex flex-row justify-center items-center p-8 px-[54px] gap-2 absolute w-[641px] left-[calc(50%-320.5px)] top-[120px] bg-white border border-[#E4E7EC] shadow-xl rounded-lg">
              <div className="flex items-center">
                <Typography
                  variant="h4"
                  className="font-inter font-bold text-2xl text-[#0C111D] mr-1"
                  placeholder={undefined}
                  onResize={undefined}
                  onResizeCapture={undefined}
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                >
                  {getSystemStatus().prefix}
                </Typography>
                <Typography
                  variant="h4"
                  className={`font-inter font-bold text-2xl ${getSystemStatus().color}`}
                  placeholder={undefined}
                  onResize={undefined}
                  onResizeCapture={undefined}
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                >
                  {getSystemStatus().status}
                </Typography>
              </div>
            </div>
          )}
        </div>
      </motion.header>
      {/* Main Content - White */}
      <main className="flex-grow bg-white pt-8 flex flex-col pb-[48px]">
        <div className="w-full mx-auto px-4 py-4 flex-grow">
          {/* Current Status Section */}
          <div className="mb-6">
            <Typography
              variant="h2"
              className="text-gray-500 text-sm"
              placeholder={undefined}
              onResize={undefined}
              onResizeCapture={undefined}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            >
              {t("Current")}
            </Typography>
            <Typography
              variant="h3"
              className="text-xl font-bold text-gray-800"
              placeholder={undefined}
              onResize={undefined}
              onResizeCapture={undefined}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            >
              {t("Status Of Systems")}
            </Typography>
          </div>

          {/* Services Grid with Accordions */}
          {metricsLoading && metrics.length === 0 ? (
            <Loading />
          ) : metricsError ? (
            <div className="text-center py-8 text-red-500">
              {t("Error loading pod metrics:")} {metricsError}
            </div>
          ) : Object.keys(podsByContainer).length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {t("No pods available for the selected cluster")}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8 auto-rows-min">
              {Object.entries(podsByContainer).map(([containerName, pods]) => (
                <div key={containerName} className="grid-item">
                  <PodServiceCard
                    containerName={containerName}
                    pods={pods}
                    // lag={lagByClientId[pods[0].container_name] || "-"}
                    lag={
                      kafkaLagMetrics.filter(
                        (lag) =>
                          lag.clientId == pods[0].container_name ||
                          lag.clientId.replace("-service", "") ==
                            pods[0].container_name
                      ) || "-"
                    }
                    isCritical={
                      criticalServices?.some(cs => 
                        cs.service_name === (pods[0]?.container_name || containerName) && cs.enabled
                      )
                    }
                  />
                </div>
              ))}
            </div>
          )}

          {/* Last 30 days header */}
          <div className="mb-2 flex justify-between items-center">
            <div>
              <Typography
                variant="h2"
                className="text-gray-500 text-sm"
                placeholder={undefined}
                onResize={undefined}
                onResizeCapture={undefined}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              >
                {t("Period from")}{" "}
                {`${selectedYear}-${String(selectedMonth + 1).padStart(2, "0")}-01`}{" "}
                {t("to")}{" "}
                {`${selectedYear}-${String(selectedMonth + 1).padStart(2, "0")}-${String(days.length).padStart(2, "0")}`}
              </Typography>
              <Typography
                variant="h3"
                className="text-xl font-bold text-gray-800"
                placeholder={undefined}
                onResize={undefined}
                onResizeCapture={undefined}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              >
                Systems
              </Typography>
            </div>
            <MonthNavigator
              selectedYear={selectedYear}
              selectedMonth={selectedMonth}
              onChange={handleMonthChange}
            />
          </div>

          {/* Status Calendar Table Component */}
          <StatusCalendarTable
            days={days}
            podsByContainer={podsByContainer}
            lagByClientId={lagByClientId}
            metricsLoading={metricsLoading}
            metricsError={metricsError}
            selectedYear={selectedYear}
            selectedMonth={selectedMonth}
            currentMonth={currentMonth}
            currentYear={currentYear}
            todayDate={todayDate}
            metricsByDayAndPod={metricsByDayAndPod}
            handleHistoricalMetricsClick={handleHistoricalMetricsClick}
          />
        </div>
      </main>
      {/* Footer - Dark */}
      <footer className="bg-[#0C111D] text-white py-3 fixed bottom-0 left-0 right-0 z-10">
        <div className="flex flex-row justify-between items-center px-6">
          <div className="font-inter font-bold text-sm text-[#FCFCFD]">
            {systemType === "prod" ? "Production System" : "Development System"}
          </div>
          <div className="flex flex-row">
            <div className="mr-6">
              <p className="font-inter font-normal text-sm text-[#12B76A]">
                {t("Operational")}{" "}
                {(() => {
                  // Get services in maintenance
                  const servicesInMaintenance = new Set<string>();
                  
                  // Check if global maintenance is active
                  if (globalMaintenance?.enabled && isMaintenanceActive(globalMaintenance)) {
                    // If global maintenance is active, all services are in maintenance
                    Object.keys(podsByContainer).forEach(containerName => {
                      servicesInMaintenance.add(containerName);
                    });
                  } else if (activeSessions && activeSessions.length > 0) {
                    // Get services from active maintenance sessions
                    const activeMaintenanceSessions = activeSessions.filter(
                      session => session.enabled && isMaintenanceActive(session)
                    );
                    
                    activeMaintenanceSessions.forEach(session => {
                      if (session.services && Array.isArray(session.services)) {
                        session.services.forEach(service => {
                          // Find matching container names
                          Object.entries(podsByContainer).forEach(([containerName, pods]) => {
                            const containerNameLower = containerName.toLowerCase();
                            const serviceLower = service.toLowerCase();
                            if (pods.some(pod => pod.container_name.toLowerCase() === serviceLower) || 
                                containerNameLower === serviceLower) {
                              servicesInMaintenance.add(containerName);
                            }
                          });
                        });
                      }
                    });
                  }
                  
                  // Count operational services (not in maintenance)
                  let operationalCount = 0;
                  Object.entries(podsByContainer).forEach(([containerName, pods]) => {
                    // Skip services in maintenance
                    if (servicesInMaintenance.has(containerName)) {
                      return;
                    }
                    // Check if all pods are running
                    const allPodsRunning = pods.every(pod => pod.phase === "Running");
                    if (allPodsRunning) {
                      operationalCount++;
                    }
                  });
                  
                  return operationalCount;
                })()}
              </p>
            </div>
            <div className="mr-6">
              <p className="font-inter font-normal text-sm text-[#FDB022]">
                {t("Degraded")}{" "}
                {(() => {
                  // Get services in maintenance
                  const servicesInMaintenance = new Set<string>();
                  
                  // Check if global maintenance is active
                  if (globalMaintenance?.enabled && isMaintenanceActive(globalMaintenance)) {
                    // If global maintenance is active, all services are in maintenance
                    Object.keys(podsByContainer).forEach(containerName => {
                      servicesInMaintenance.add(containerName);
                    });
                  } else if (activeSessions && activeSessions.length > 0) {
                    // Get services from active maintenance sessions
                    const activeMaintenanceSessions = activeSessions.filter(
                      session => session.enabled && isMaintenanceActive(session)
                    );
                    
                    activeMaintenanceSessions.forEach(session => {
                      if (session.services && Array.isArray(session.services)) {
                        session.services.forEach(service => {
                          // Find matching container names
                          Object.entries(podsByContainer).forEach(([containerName, pods]) => {
                            const containerNameLower = containerName.toLowerCase();
                            const serviceLower = service.toLowerCase();
                            if (pods.some(pod => pod.container_name.toLowerCase() === serviceLower) || 
                                containerNameLower === serviceLower) {
                              servicesInMaintenance.add(containerName);
                            }
                          });
                        });
                      }
                    });
                  }
                  
                  // Count degraded services (not in maintenance)
                  let degradedCount = 0;
                  Object.entries(podsByContainer).forEach(([containerName, pods]) => {
                    // Skip services in maintenance
                    if (servicesInMaintenance.has(containerName)) {
                      return;
                    }
                    // Check if any pod is pending but none are failed
                    const hasPendingPods = pods.some(pod => pod.phase === "Pending");
                    const hasFailedPods = pods.some(pod => pod.phase === "Failed");
                    if (hasPendingPods && !hasFailedPods) {
                      degradedCount++;
                    }
                  });
                  
                  return degradedCount;
                })()}
              </p>
            </div>
            <div className="mr-6">
              <p className="font-inter font-normal text-sm text-[#F04438]">
                {t("Outage")}{" "}
                {(() => {
                  // Get services in maintenance
                  const servicesInMaintenance = new Set<string>();
                  
                  // Check if global maintenance is active
                  if (globalMaintenance?.enabled && isMaintenanceActive(globalMaintenance)) {
                    // If global maintenance is active, all services are in maintenance
                    Object.keys(podsByContainer).forEach(containerName => {
                      servicesInMaintenance.add(containerName);
                    });
                  } else if (activeSessions && activeSessions.length > 0) {
                    // Get services from active maintenance sessions
                    const activeMaintenanceSessions = activeSessions.filter(
                      session => session.enabled && isMaintenanceActive(session)
                    );
                    
                    activeMaintenanceSessions.forEach(session => {
                      if (session.services && Array.isArray(session.services)) {
                        session.services.forEach(service => {
                          // Find matching container names
                          Object.entries(podsByContainer).forEach(([containerName, pods]) => {
                            const containerNameLower = containerName.toLowerCase();
                            const serviceLower = service.toLowerCase();
                            if (pods.some(pod => pod.container_name.toLowerCase() === serviceLower) || 
                                containerNameLower === serviceLower) {
                              servicesInMaintenance.add(containerName);
                            }
                          });
                        });
                      }
                    });
                  }
                  
                  // Count outage services (not in maintenance)
                  let outageCount = 0;
                  Object.entries(podsByContainer).forEach(([containerName, pods]) => {
                    // Skip services in maintenance
                    if (servicesInMaintenance.has(containerName)) {
                      return;
                    }
                    // Check if any pod is failed
                    const hasFailedPods = pods.some(pod => pod.phase === "Failed");
                    if (hasFailedPods) {
                      outageCount++;
                    }
                  });
                  
                  return outageCount;
                })()}
              </p>
            </div>
            <div className="mr-6">
              <p className="font-inter font-normal text-sm text-[#8098F9]">
                {t("Maintenance")}{" "}
                {(() => {
                  // Count services in maintenance mode
                  let maintenanceCount = 0;
                  
                  // Check if global maintenance is active
                  if (globalMaintenance?.enabled && isMaintenanceActive(globalMaintenance)) {
                    // If global maintenance is active, count all services
                    maintenanceCount = Object.keys(podsByContainer).length;
                  } else if (activeSessions && activeSessions.length > 0) {
                    // Count services in active maintenance sessions
                    const activeMaintenanceSessions = activeSessions.filter(
                      session => session.enabled && isMaintenanceActive(session)
                    );
                    
                    // Get unique service names from all active maintenance sessions
                    const servicesInMaintenance = new Set<string>();
                    activeMaintenanceSessions.forEach(session => {
                      if (session.services && Array.isArray(session.services)) {
                        session.services.forEach(service => {
                          // Find matching container names
                          Object.entries(podsByContainer).forEach(([containerName, pods]) => {
                            if (pods[0]?.container_name === service || containerName === service) {
                              servicesInMaintenance.add(containerName);
                            }
                          });
                        });
                      }
                    });
                    
                    maintenanceCount = servicesInMaintenance.size;
                  }
                  
                  return maintenanceCount;
                })()}
              </p>
            </div>
          </div>
        </div>
      </footer>
      {/* Historical Metrics Dialog */}
      <HistoricalMetricsDialog
        open={historyDialogOpen}
        handleOpen={handleHistoryDialogToggle}
        data={selectedHistoricalData}
      />
    </div>
  );
};
